<template>
    <div>
        <div class="document-list">
            <el-upload
                v-if="allowUpload"
                class="mb-2"
                ref="elUpload"
                :action="documentUploadUrl"
                :headers="attachmentRequestHeaders"
                :on-success="handleDocumentUploadSuccess"
                :before-remove="removeDocument"
                :on-error="handleDocumentUploadError"
                :on-preview="viewDocument"
                :on-change="handleChange"
                :beforeUpload="beforeDocumentUpload">
                <div slot="trigger" class="text-center">
                    <vue-dropzone v-if="dropzoneUrl"
                                  id="document-list-dropzone"
                                  ref="documentDropzone"
                                  class="transition ease-in-out duration-100"
                                  :options="dropzoneOptions"
                                  :use-custom-slot=true
                                  :include-styling="false"
                                  @vdropzone-drag-over="dragOver"
                                  @vdropzone-drag-leave="dragLeave"
                                  @vdropzone-drop="fileDropped"
                                  @vdropzone-error="checkForFileErrors"
                                  @vdropzone-success="fileUploaded"
                                  @vdropzone-upload-progress="processingFileUpload"
                                  @vdropzone-complete="fileUploadCompletedOrFailed">
                        <button class="btn btn-block btn-outline-primary">
                            Click or drag a file here to upload document
                        </button>
                    </vue-dropzone>
                    <button class="btn btn-block btn-outline-primary" v-if="!dropzoneUrl">
                        Click to upload document
                    </button>
                    <transition
                        enter-to-class="opacity-100"
                        leave-class="opacity-100"
                        enter-active-class="transition duration-1000 ease-in"
                        leave-active-class="transition duration-1000 ease-in"
                        enter-class="opacity-0"
                        leave-to-class="opacity-0">
                        <div v-if="processingUpload" class="flex-col h-8 w-full mt-2">
                            <div class="flex justify-between">
                                <span class="truncate">
                                    {{ fileName }}
                                </span>
                                <span class="">
                                    {{ processingPercentage }}%
                                </span>
                            </div>
                            <div class="border-b-2 border-blue-500" :style="'width:' + processingPercentage +'%'">
                            </div>
                        </div>
                    </transition>
                </div>
            </el-upload>

            <div v-if="loading" class="text-gray-500 flex justify-center items-center py-2">
                <icon-refresh class="h-4 w-4 animate-spin text-indigo-600"/> &nbsp;Loading Documents...
            </div>

            <div class="custom-scroll">
                <div class="row scroll-pane">
                    <table class="table table-striped" style="table-layout: fixed">
                        <thead>
                            <tr>
                                <th width="30px;"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>

                            <tr v-for="(document, index) in documents"
                                :key="document.id">

                                <td><i :class="documentIcon(document.mime_type)" class="document-type-icon"></i></td>

                                <td>
                                    <div class="date-label-container">
                                        <span class="document-create-date">{{ document.created_at | datetime }}</span>
                                        <span class="label label-sm label-light-info pull-right"
                                              v-if="document.document_type">{{ document.document_type | capitalize }}</span>
                                    </div>
                                    <a :href="document.url" target="_blank" class="document-name document-name-hover"
                                       @mouseover="showClickToViewHint(document.id)"
                                       @mouseleave="showClickToViewHint(-1)">
                                        <p>{{ document.name }}</p>
                                    </a>

                                    <div class="document-actions-container mt-1">
                                        <button class="btn btn-rounded btn-outline-primary btn-xs mr-1"
                                                @click.prevent="editDocument(document)">
                                            Edit
                                        </button>
                                        <button class="btn btn-rounded btn-outline-primary btn-xs mr-1"
                                                @click.prevent="shareDocument(document)"
                                                v-if="allowShare">
                                            Email
                                        </button>
                                        <button class="btn btn-rounded btn-outline-danger btn-xs mr-1"
                                                @click.prevent="confirmDocumentDelete(document)">
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!--EDIT MODAL-->
        <div id="document-edit-modal" class="modal fade" tabindex="-1" role="dialog" style="z-index: 2000">
            <div class="modal-dialog">
                <div class="modal-content" v-if="selectedDocument">
                    <div class="modal-header">
                        <h4 class="modal-title font-normal">{{ documentEditForm.name }}</h4>
                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">×</button>
                    </div>
                    <div class="modal-body">
                        <div class="form">
                            <div class="form-group">
                                <label for="document_name">Name</label>
                                <input class="form-control" type="text" id="document_name"
                                       v-model="documentEditForm.name"
                                       :class="{'is-invalid': documentEditForm.errors.has('name')}">

                                <span class="invalid-feedback" v-show="documentEditForm.errors.has('name')">
                                    {{ documentEditForm.errors.get("name") }}
                                </span>
                            </div>

                            <div class="form-group">
                                <label for="name">Type</label>
                                <div id="name">
                                    <el-autocomplete
                                        class="inline-input w-100"
                                        v-model="documentEditForm.document_type"
                                        :fetch-suggestions="typeListSearch"
                                        placeholder="Enter document type"
                                    ></el-autocomplete>
                                </div>
                                <span class="invalid-feedback"
                                      :class="{'d-block': documentEditForm.errors.has('document_type')}"
                                      v-show="documentEditForm.errors.has('document_type')">
                                    {{ documentEditForm.errors.get("document_type") }}
                                </span>
                            </div>

                            <div class="row">
                                <div class="col-sm-7">
                                    <small class="text-muted">Created {{ selectedDocument.created_at | datetime }}</small>
                                </div>
                                <div class="col-sm-5">
                                    <small class="text-muted pull-right">Size {{ selectedDocument.human_readable_size }}
                                    </small>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-success waves-effect waves-light"
                                @click.prevent="save()"
                                :disabled="documentEditForm.busy">Save
                        </button>
                        <button type="button" class="btn btn-default waves-effect" data-dismiss="modal"
                                :disabled="documentEditForm.busy">Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DropzoneOptions from "../../mixins/dropzone-options";
import AcceptedMimeTypes from "../../mixins/accepted-mime-types";

export default {
    mixins: [DropzoneOptions, AcceptedMimeTypes],

    props: {
        documentListUrl: {},
        documentUploadUrl: {},
        allowShare: {
            default: true
        },
        allowUpload: {
            default: true
        },
    },

    data () {
        return {
            loading: true,
            documents: [],
            uploadedDocuments: [],
            attachmentRequestHeaders: window.axios.defaults.headers.common,
            selectedDocument: undefined,
            documentEditForm: new AppForm({}),
            shouldShowViewHintForDocument: -1
        };
    },

    created () {
        this.fetchDocuments();
    },

    watch: {
        documentListUrl (newUrl) {
            this.fetchDocuments();
        }
    },

    mounted () {
        let self = this;

        Bus.$on("document-added", function (document) {
            if (document && document.uuid !== undefined) {
                self.documents.unshift(document);
            }
        });

        Bus.$on("task-card-added", function () {
            self.fetchDocuments(8000);
        });

        // Still needed for legacy data, but new quote requests are not being generated
        Bus.$on("quote-request-sent", function () {
            self.fetchDocuments(8000);
        });

        Bus.$on("edit-document", function (document) {
            self.editDocument(document);
        });

    },

    methods: {

        showClickToViewHint (documentId) {
            this.shouldShowViewHintForDocument = documentId;
        },

        documentIcon (mimeType) {

            if (["image/gif", "image/jpeg", "image/png", "image/svg+xml"].includes(mimeType)) {
                return "mdi mdi-file-image";
            }

            if (["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(mimeType)) {
                return "mdi mdi-file-word";
            }

            if (["application/pdf"].includes(mimeType)) {
                return "mdi mdi-file-pdf";
            }

            if (["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"].includes(mimeType)) {
                return "mdi mdi-file-powerpoint";
            }

            if (["application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.oasis.opendocument.spreadsheet"].includes(mimeType)) {
                return "mdi mdi-file-excel";
            }

            if (["application/x-tar", "application/zip", "application/x-7z-compressed"].includes(mimeType)) {
                return "mdi mdi-zip-box";
            }

            return "mdi mdi-file-document";

        },

        fetchDocuments (bufferTime = 0) {
            if (this.documentListUrl === null) {
                return;
            }

            this.loading = true;
            this.$emit("start-loading-documents");

            // Use bufferTime when documents are created in jobs
            this.sleep(bufferTime).then(() => {
                axios.get(this.documentListUrl)
                .then(response => {
                    this.documents = response.data;
                    this.$emit("finish-loading-documents");
                    this.loading = false;
                })
                .catch(errors => {
                    this.loading = false;
                    this.$emit("finish-loading-documents");
                    toastr.error("Error loading documents: " + errors.message);
                });
            });
        },

        handleDocumentUploadSuccess (response, file, fileList) {

            if (!this.documents) {
                this.documents = [];
            }

            let index = this.$refs.elUpload.uploadFiles.indexOf(file);
            this.$refs.elUpload.uploadFiles.splice(index, 1);
        },

        handleDocumentUploadError (error, file, fileList) {
            toastr.error("Document upload failed! Please try again.");
        },

        beforeDocumentUpload (file) {

            const isLt10M = file.size / 1024 / 1024 < 10;

            if (!isLt10M) {
                toastr.error("File size can not exceed 10MB!");
            }

            return isLt10M;

        },

        handleChange (file, fileList) {

            if (file.response) {
                this.documents.unshift(file.response);
            }

        },

        removeDocument (file, fileList) {

            if (file === undefined || (file.id === undefined && file.response === undefined)) {
                return true;
            }

            let mediaId = file.id !== undefined ? file.id : file.response.id;

            return new Promise((resolve, reject) => {

                axios.post("/documents/" + mediaId + "/delete", {_method: "DELETE"})
                .then(response => {
                    this.documents.splice(this.documents.indexOf(file), 1);
                    resolve(response.data);
                })
                .catch(errors => {
                    toastr.error(errors.response.data);
                    reject(errors.response);
                });

            });

        },

        viewDocument (file) {
            window.open(file.url, "_blank");
        },

        editDocument (file) {

            this.selectedDocument = file;

            this.documentEditForm = new AppForm({
                name: this.selectedDocument.name,
                document_type: this.selectedDocument.document_type
            });

            $("#document-edit-modal").modal("show");

        },

        typeListSearch (query, cb) {

            let documentTypes = [
                {"value": "Invoice"},
                {"value": "Quote"},
                {"value": "Jobcard"},
                {"value": "Taskcard"},
                {"value": "Notice"},
                {"value": "Inspection"},
                {"value": "Image"},
                {"value": "Lease"}
            ];

            var results = query ? documentTypes.filter(this.createDocumentTypeFilter(query)) : documentTypes;

            cb(results);

        },

        createDocumentTypeFilter (queryString) {
            return (type) => {
                return (type.value.toLowerCase().indexOf(queryString.toLowerCase()) === 0);
            };
        },

        save () {

            App.patch("/media/" + this.selectedDocument.id + "/update", this.documentEditForm)
            .then((response) => {
                toastr.success("Document updated!");

                const index = this.documents.findIndex(doc => doc.id === response.id);
                this.$set(this.documents, index, response);

                $("#document-edit-modal").modal("hide");
                this.selectedDocument = undefined;
            });

        },

        confirmDocumentDelete (document) {

            let self = this;
            swal({
                type: "warning",
                title: "Delete Document",
                text: "Are you sure you want to delete " + document.name + "?",
                showCancelButton: true
            }).then((result) => {
                if (result.value) {
                    self.deleteDocument(document);
                }
            });

        },

        deleteDocument (document) {
            axios.post("/media/" + document.id + "/delete", {_method: "DELETE"})
            .then(() => {

                const index = this.documents.indexOf(document);

                if (index !== -1) {
                    this.documents.splice(index, 1);
                }

                toastr.success(document.name + " deleted!");

            })
            .catch(errors => {
                toastr.error("Document could not be deleted! " + errors.message);
            });
        },

        shareDocument (document) {
            this.$emit("share-document", document);
        },

        dragOver (event) {
            // Add some visual fluff to show the user can drop its files
            if (!event.currentTarget.children[0].childNodes[0].classList.contains("border-dashed")) {
                event.currentTarget.children[0].childNodes[0].classList.add("bg-blue-50", "border-dashed", "border-gray-400");
            }
        },

        dragLeave (event) {
            // Clean up
            event.currentTarget.children[0].childNodes[0].classList.remove("bg-blue-50", "border-dashed", "border-gray-400");
        },

        fileDropped (event) {
            this.dragLeave(event);
            this.fetchDocuments();
        },

        checkForFileErrors (file, message, xhr) {
            const isLt10M = file.size / 1024 / 1024 < 10;

            if (!isLt10M) {
                toastr.error("File size can not exceed 10MB!");
            }

            //check if file is part of application accepted mime-types
            if (!this.documentMimeTypes.includes(file.type) || file.type === "") {
                toastr.error("That file type is not supported. Please try more commonly used file types.");
            }
        },

        fileUploaded (file, response) {
            toastr.success(this.fileName + " has successfully uploaded");
            this.fetchDocuments();
        },

        // sleep time expects milliseconds
        sleep (time) {
            return new Promise((resolve) => setTimeout(resolve, time));
        },
    },
};
</script>

<style>

.document-list {
    min-height: 400px;
}

.document-name p {
    font-size: 12px;
    line-height: 16px;
    margin-top: 4px;
    margin-bottom: 6px;
    color: #54667a;
}

.document-create-date {
    line-height: 14px;
    font-size: 12px;
    color: #9e9e9e;
}

.document-type-icon {
    font-size: 24px;
    color: #6faaf3;
}

.date-label-container {
    line-height: 1;
    padding-top: 4px;
}

.document-actions-container {
    padding-bottom: 4px;
}

.document-name-hover p:hover {
    text-decoration: underline;
    text-decoration-color: #0f8edd;
}

</style>
